import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const GHL_CERT_URL = "https://directory.gohighlevel.com/india/pune/certified-admins/kiran-kshirsagar";
const GHL_BADGE_URL = "https://customer-assets-agu9un31.emergentagent.net/job_beacon-tech-labs/artifacts/6b4y1mad_09d68805-ef5b-4f16-9b67-ccce0bf65473.webp";

const GROUPS = [
  {
    label: "Backend Engineering",
    items: ["PHP", "Laravel", "Node.js", "Express"],
  },
  {
    label: "Databases",
    items: ["MySQL", "PostgreSQL", "MongoDB", "Redis"],
  },
  {
    label: "Frontend Engineering",
    items: ["React.js", "JavaScript ES6+", "Tailwind CSS", "Bootstrap", "HTML5", "CSS3"],
  },
  {
    label: "Cloud & Infrastructure",
    items: ["AWS EC2", "AWS S3", "AWS Lambda", "RDS", "CloudFront", "DevOps"],
  },
  {
    label: "Agentic AI-Augmented Development",
    items: ["Cursor", "Claude", "Replit", "Codex"],
    accent: true,
  },
  {
    label: "Platforms",
    items: ["GoHighLevel", "WordPress", "Shopify"],
  },
  {
    label: "Automation & Integrations",
    items: ["Zapier", "Stripe API", "PayPal API", "Authorize.net", "NMI", "REST", "GraphQL"],
  },
];

export default function TechStack() {
  return (
    <section
      id="technology"
      data-testid="tech-stack-section"
      className="relative border-t border-hairline py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-8 mb-16 md:mb-20 items-end">
          <div className="md:col-span-3">
            <span className="mono-label">[03] Technology</span>
          </div>
          <div className="md:col-span-9">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-15% 0px" }}
              transition={{ duration: 0.7 }}
              className="font-display text-4xl md:text-6xl lg:text-7xl tracking-[-0.03em] leading-[0.98] max-w-[22ch]"
            >
              A stack chosen for
              <span className="text-[color:var(--accent)]"> longevity</span>,
              not <span className="text-white/40">trend</span>.
            </motion.h2>
          </div>
        </div>

        <div className="grid gap-8 md:gap-0">
          {GROUPS.map((g, gi) => (
            <motion.div
              key={g.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10% 0px" }}
              transition={{ duration: 0.5, delay: gi * 0.06 }}
              data-testid={`tech-group-${gi}`}
              className="grid md:grid-cols-12 gap-6 md:gap-10 py-8 md:py-10 border-t border-hairline"
            >
              <div className="md:col-span-3">
                <div className="mono-label !text-white/50">[{String(gi + 1).padStart(2, "0")}]</div>
                <h3 className={`mt-2 font-display text-xl md:text-2xl tracking-tight ${g.accent ? "text-[color:var(--accent)]" : ""}`}>
                  {g.label}
                </h3>
              </div>
              <div className="md:col-span-9 flex flex-wrap gap-2 md:gap-3 items-start">
                {g.items.map((it) => (
                  <span
                    key={it}
                    className={`font-mono text-[12px] px-3 py-2 border ${
                      g.accent
                        ? "border-[color:var(--accent)]/50 text-white hover:bg-[color:var(--accent)] hover:border-[color:var(--accent)]"
                        : "border-hairline text-white/85 hover:border-white/60"
                    } transition-colors cursor-default`}
                  >
                    {it}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
          <div className="border-t border-hairline" />
        </div>

        {/* Certified Partner Trust Seal */}
        <motion.div
          data-testid="ghl-partner-badge"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10% 0px" }}
          transition={{ duration: 0.7 }}
          className="mt-16 md:mt-20 relative overflow-hidden border border-[color:var(--accent)]/40 bg-[color:var(--accent)]/[0.06]"
        >
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "radial-gradient(45% 60% at 15% 50%, rgba(255,59,48,0.18), transparent 70%)",
            }}
          />

          <div className="relative grid md:grid-cols-12 gap-6 md:gap-10 p-8 md:p-12 items-center">
            <div className="md:col-span-3 flex md:justify-center">
              <a
                href={GHL_CERT_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Verify HighLevel Certified Admin — Kiran Kshirsagar"
                className="group/badge relative inline-block transition-transform hover:-translate-y-1 hover:scale-[1.02]"
              >
                <img
                  src={GHL_BADGE_URL}
                  alt="HighLevel Certified Admin badge"
                  className="w-32 h-32 md:w-40 md:h-40 object-contain drop-shadow-[0_10px_30px_rgba(0,0,0,0.6)]"
                  loading="lazy"
                />
                <span
                  aria-hidden
                  className="pointer-events-none absolute -inset-2 border border-[color:var(--accent)]/0 group-hover/badge:border-[color:var(--accent)]/60 transition-colors"
                />
              </a>
            </div>
            <div className="md:col-span-6">
              <div className="mono-label !text-[color:var(--accent)]">Certified Partner</div>
              <h3 className="mt-3 font-display text-2xl md:text-3xl lg:text-[36px] leading-[1.05] tracking-[-0.02em]">
                HighLevel Certified Admin
              </h3>
              <p className="mt-3 text-white/70 text-[14px] md:text-[15px] leading-[1.6] max-w-[58ch]">
                Officially certified to design, build and white-label GoHighLevel sub-accounts —
                funnels, pipelines, SMS/email automation, snapshots and multi-client agency
                infrastructure at scale.
              </p>
              <a
                href={GHL_CERT_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="ghl-verify-link"
                className="mt-4 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.18em] uppercase text-white/80 hover:text-[color:var(--accent)] transition-colors"
              >
                Verify on GoHighLevel directory <ArrowUpRight size={14} />
              </a>
            </div>
            <div className="md:col-span-3 flex md:justify-end">
              <a
                href="#contact"
                className="group inline-flex items-center gap-2 border border-[color:var(--accent)] hover:bg-[color:var(--accent)] px-5 py-3 text-[13px] font-medium tracking-tight transition-colors"
              >
                Talk to a certified expert
                <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
